import { useEffect } from 'react';
import { ActionIcon, Button, Indicator, Text, Tooltip } from '@mantine/core';
import { useClipboard, useFullscreenDocument } from '@mantine/hooks';
import {
    IconArrowsDiagonal,
    IconArrowsDiagonalMinimize2,
    IconCheck,
    IconHandStop,
    IconLink,
    IconMessage2,
    IconUsers,
} from '@tabler/icons-react';
import { useParticipants, useRoomInfo } from '@livekit/components-react';
import { useTranslation } from 'react-i18next';

import { CallDuration } from '../call-duration';
import { useLobby } from '../lobby/lobby';
import { useRaisedHands } from '../reactions';
import { RecordingStatus } from '../recording-indicator';
import { CALL_SHORTCUTS } from './shortcuts';
import classes from './top-bar.module.css';
import { i18n } from '@/i18n';
import { notifyError } from '@/shared/notify';
import { ShortcutHint } from '@/shared/shortcut-hint';

export type CallPanel = 'chat' | 'participants';

export function TopBar({
    openPanel,
    unreadMessages,
    onTogglePanel,
}: Readonly<{ openPanel: CallPanel | null; unreadMessages: number; onTogglePanel: (panel: CallPanel) => void }>) {
    const { t } = useTranslation('conference');
    const { fullscreen, toggle: toggleFullscreen } = useFullscreenDocument();
    const clipboard = useClipboard({ timeout: 2000 });
    const { name: roomName } = useRoomInfo();
    const participantCount = useParticipants().length;
    const raisedHandCount = useRaisedHands().length;
    const waitingCount = useLobby()?.requests.length ?? 0;

    useEffect(() => {
        if (clipboard.error) notifyError(i18n.t('conference:topBar.copyLinkError'), clipboard.error);
    }, [clipboard.error]);

    const fullscreenLabel = fullscreen ? t('topBar.exitFullscreen') : t('topBar.enterFullscreen');
    const copyLinkLabel = clipboard.copied ? t('topBar.linkCopied') : t('topBar.copyLink');
    const chatOpen = openPanel === 'chat';
    const chatLabel = chatOpen ? t('topBar.closeChat') : t('topBar.openChat');
    const participantsLabel = t('topBar.participants', { count: participantCount });
    const raisedHandsLabel = t('topBar.raisedHands', { count: raisedHandCount });

    return (
        <header className={classes.bar}>
            <div className={classes.side}>
                <Tooltip label={<ShortcutHint keys={CALL_SHORTCUTS.fullscreen} label={fullscreenLabel} />}>
                    <ActionIcon
                        aria-label={fullscreenLabel}
                        radius="var(--radius-rounded-full)"
                        variant="default"
                        visibleFrom="sm"
                        onClick={() => void toggleFullscreen()}
                    >
                        {fullscreen ? <IconArrowsDiagonalMinimize2 size={16} /> : <IconArrowsDiagonal size={16} />}
                    </ActionIcon>
                </Tooltip>
                <Tooltip label={copyLinkLabel}>
                    <ActionIcon
                        aria-label={copyLinkLabel}
                        color={clipboard.copied ? 'success' : undefined}
                        radius="var(--radius-rounded-full)"
                        variant={clipboard.copied ? 'light' : 'default'}
                        onClick={() => {
                            clipboard.copy(`${location.origin}/${encodeURIComponent(roomName)}${location.hash}`);
                        }}
                    >
                        {clipboard.copied ? <IconCheck size={16} /> : <IconLink size={16} />}
                    </ActionIcon>
                </Tooltip>
            </div>
            <div className={classes.center}>
                {raisedHandCount > 0 && (
                    <Tooltip label={raisedHandsLabel}>
                        <Button
                            aria-label={raisedHandsLabel}
                            color="success"
                            leftSection={<IconHandStop size={16} />}
                            radius="var(--radius-rounded-full)"
                            variant="filled"
                            onClick={() => {
                                if (openPanel !== 'participants') onTogglePanel('participants');
                            }}
                        >
                            {raisedHandCount}
                        </Button>
                    </Tooltip>
                )}
                <div className={classes.title}>
                    <Text truncate fw={500} size="sm">
                        {roomName}
                    </Text>
                    <CallDuration />
                    <RecordingStatus />
                </div>
                <Indicator color="action" disabled={waitingCount === 0} label={waitingCount} size="var(--spacing-4)">
                    <Tooltip label={<ShortcutHint keys={CALL_SHORTCUTS.participants} label={participantsLabel} />}>
                        <Button
                            aria-label={participantsLabel}
                            aria-pressed={openPanel === 'participants'}
                            leftSection={<IconUsers size={20} />}
                            radius="var(--radius-rounded-full)"
                            variant={openPanel === 'participants' ? 'light' : 'default'}
                            onClick={() => {
                                onTogglePanel('participants');
                            }}
                        >
                            {participantCount}
                        </Button>
                    </Tooltip>
                </Indicator>
            </div>
            <div className={classes.side}>
                <Indicator
                    color="action"
                    disabled={unreadMessages === 0}
                    label={unreadMessages > 99 ? '99+' : unreadMessages}
                    size="var(--spacing-4)"
                >
                    <Tooltip label={<ShortcutHint keys={CALL_SHORTCUTS.chat} label={chatLabel} />}>
                        <ActionIcon
                            aria-pressed={chatOpen}
                            radius="var(--radius-rounded-full)"
                            variant={chatOpen ? 'light' : 'default'}
                            aria-label={
                                unreadMessages > 0 ? `${chatLabel}. ${t('topBar.unread', { count: unreadMessages })}` : chatLabel
                            }
                            onClick={() => {
                                onTogglePanel('chat');
                            }}
                        >
                            <IconMessage2 size={16} />
                        </ActionIcon>
                    </Tooltip>
                </Indicator>
            </div>
        </header>
    );
}
